import { useMemo } from "react";
import { useSelector } from "react-redux";
import { RootState } from "store/reducers";
import { displayPlanName } from "helpers/stripe";

export const usePauseStatus = () => {
    const { userInfo } = useSelector((s: RootState) => s.user);

    const userIsOrWillPause = useMemo<boolean>(
        () => userInfo?.Plan?.Schedule?.Details?.PausedPlanId !== null,
        [userInfo?.Plan?.Schedule?.Details?.PausedPlanId]
    );

    const userIsPaused = useMemo<boolean>(
        () => userInfo.Claims?.Details?.Roles.includes("PausedV2") ?? false,
        [userInfo]
    );

    const userWillPause = useMemo<boolean>(
        () => userIsOrWillPause && !userIsPaused,
        [userIsOrWillPause, userIsPaused]
    );

    const pausedPlanId = useMemo<string | undefined>(
        () => userInfo?.Plan?.Schedule?.Details?.PausedPlanId,
        [userInfo]
    );

    const pausedPlanName = useMemo<string | undefined>(
        () => displayPlanName(userInfo?.Plan?.Schedule?.Details?.PausedPlanName),
        [userInfo]
    );

    const waitingOnReactivation = useMemo<boolean>(
        () => !userIsOrWillPause && userIsPaused,
        [userIsOrWillPause, userIsPaused]
    );

    return {
        /**
         * Whether the user is currently paused.
         */
        userIsPaused,
        /**
         * Whether the user will be paused in the future.
         */
        userWillPause,
        /**
         * Whether the user is currently paused or will be paused in the future.
         */
        userIsOrWillPause,
        /**
         * Whether the user is currently waiting on reactivation.
         * This accounts for the state the account is in after the pause schedule has been released but the subscription has not updated via the webhook.
         * This state usually lasts for a few seconds.
         */
        waitingOnReactivation,
        /**
         * The plan that the user is currently on, if they are paused. (Business Monthly, etc.)
         */
        pausedPlanId,
        /**
         * The display name of the plan that the user is currently 
         * on, if they are paused. (Suite Monthly, etc.)
         */
        pausedPlanName,
        /**
         * The date that the user will be pause. This is only set if the user has a pause schedule.
         */
        pauseBeginDate: userInfo?.Plan?.Schedule?.Details?.PausedBeginDate,
        /**
         * The date that the user will be unpaused. This is only set if the user is paused.
         */
        pauseEndDate: userInfo?.Plan?.Schedule?.Details?.PausedEndDate
    };
};
